/**
 * RESPONSIBILITY: hold what the annotations declared, and hand it back.
 *
 * The ordering problem this solves: property decorators run while the class body is
 * being evaluated, BEFORE the class decorator. So fields are staged in `pending`,
 * keyed by constructor, and @Section collects them when it finally runs.
 *
 * Nothing here is a decorator. Nothing here knows what a section means. It is the
 * shared state the annotation modules write to and the generators read from.
 */
import { humanize, DEFAULT_VIEW, DEFAULT_EDIT, type Audience } from '../index';
import { HIDDEN, type FieldMeta, type SectionMeta } from './types';

/** Fields collected while class body decorators run, before @Section fires. */
const pending = new Map<Function, FieldMeta[]>();

/** Finalised sections, keyed by section name. */
const sections = new Map<string, SectionMeta>();

/** Audiences declared by @EditableBy / @ViewableBy, staged until @Section finalises. */
const pendingEdit = new Map<Function, Audience>();
const pendingView = new Map<Function, Audience>();

let orderCounter = 0;

export function pushField(
  target: object,
  meta: Omit<FieldMeta, 'order' | 'label'> & { label?: string },
): void {
  // Legacy decorators pass (prototype, key). Standard (TC39) decorators pass
  // (value, context) — so `target` is undefined and every annotation silently does
  // nothing. Fail loudly instead of producing an empty form.
  if (target === undefined || target === null) {
    throw new Error(
      `Field annotation on "${meta.key}" received no target. This build is using standard ` +
        `TC39 decorators; the framework requires legacy ones. Set ` +
        `"experimentalDecorators": true and "useDefineForClassFields": false in tsconfig.json.`,
    );
  }

  const ctor = (target as { constructor: Function }).constructor;
  const list = pending.get(ctor) ?? [];
  // No label given ⇒ use the field name, humanized.
  list.push({ ...meta, label: meta.label || humanize(meta.key), order: orderCounter++ });
  pending.set(ctor, list);
}

export const stageEditable = (ctor: Function, roles: Audience) => { pendingEdit.set(ctor, roles); };
export const stageViewable = (ctor: Function, roles: Audience) => { pendingView.set(ctor, roles); };

/**
 * The visibility switch every section carries. Declared here, once, so it reaches the
 * form, the JSON Schema, the validator and the defaults through the same path as a
 * field somebody wrote by hand — there is no second mechanism to keep in step.
 *
 * Its own collapsed panel, and last: it is not what an editor came to change, and a
 * checkbox loose among the content fields is one to tick by accident.
 */
const visibilityField = (order: number): FieldMeta => ({
  key: HIDDEN,
  kind: 'bool',
  label: 'Hide this section',
  description:
    'Visitors do not see it. You still do, marked hidden, so you can put it back.',
  required: false,
  default: false,
  group: 'Visibility',
  collapsed: true,
  order,
});

/** Called by @Section: drains everything staged for this constructor. */
export function finaliseSection(
  ctor: Function, name: string, label: string, always = false,
): void {
  const fields = (pending.get(ctor) ?? []).sort((a, b) => a.order - b.order);

  if (fields.length === 0) {
    throw new Error(
      `@Section("${name}") has no annotated fields. Anything extending a section must ` +
        `carry metadata — un-annotated sections fail at boot, never in front of an editor.`,
    );
  }
  if (sections.has(name)) throw new Error(`Duplicate @Section name "${name}".`);

  // @RenderOf names a field by hand, so it is checked the moment sections register —
  // wrong within a second of starting the server, with the fix in the message. The
  // alternative is a page that silently renders nothing.
  for (const f of fields) {
    if (!f.renderOf) continue;
    const source = fields.find((x) => x.key === f.renderOf);
    const markdownFields = fields.filter((x) => x.markdown).map((x) => x.key);

    if (!source) {
      throw new Error(
        `@RenderOf("${f.renderOf}") on ${name}.${f.key} — no field named "${f.renderOf}". `
          + `Markdown fields on this section: ${markdownFields.join(', ') || '(none)'}`,
      );
    }
    if (!source.markdown) {
      throw new Error(
        `@RenderOf("${f.renderOf}") on ${name}.${f.key} — "${f.renderOf}" is not markdown. `
          + `Add markdown: true to its annotation, or point at one of: `
          + `${markdownFields.join(', ') || '(none)'}`,
      );
    }
  }

  // A section declaring its own `hidden` would give the document two meanings for one
  // key, and the last writer would win silently. Say so at boot, with the fix.
  if (fields.some((f) => f.key === HIDDEN)) {
    throw new Error(
      `@Section("${name}") declares a field named "${HIDDEN}". That key is the framework's ` +
        `own visibility switch, which every section already carries — rename the field.`,
    );
  }

  sections.set(name, {
    name,
    label,
    ctor,
    fields: always
      ? fields
      : [...fields, visibilityField(fields[fields.length - 1].order + 1)],
    viewableBy: pendingView.get(ctor) ?? DEFAULT_VIEW,
    editableBy: pendingEdit.get(ctor) ?? DEFAULT_EDIT,
  });

  pending.delete(ctor);
  pendingEdit.delete(ctor);
  pendingView.delete(ctor);
}

/* ---- lookup -------------------------------------------------------------- */

export const getSection = (name: string): SectionMeta | undefined => sections.get(name);

export function requireSection(name: string): SectionMeta {
  const s = sections.get(name);
  if (!s) {
    throw new Error(
      `Unknown section "${name}". Registered: ${[...sections.keys()].join(', ') || '(none)'}`,
    );
  }
  return s;
}

export const allSections = (): SectionMeta[] => [...sections.values()];

/**
 * Fields of a @Row class, for @List item schemas.
 *
 * Rows are never finalised, so their fields stay in `pending` forever — which is
 * exactly what makes them readable here, lazily, long after the class was declared.
 */
export const rowFields = (ctor: Function): FieldMeta[] =>
  (pending.get(ctor) ?? []).slice().sort((a, b) => a.order - b.order);
